import {
    Box, Button, Card, FormControl, FormControlLabel, IconButton,
    InputLabel, MenuItem, Select, SelectChangeEvent, Switch, Tooltip, Typography,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import { CSVLink } from 'react-csv';
import AddNewCompanyDialog from './AddNewCompanyDialog';
import {
    DataGrid,
    GridColDef,
    GridCsvGetRowsToExportParams,
    GridRenderCellParams,
    GridRowsProp,
    GridToolbar,
    GridValueFormatterParams,
    gridSortedRowIdsSelector,
} from '@mui/x-data-grid';
import {useDispatch, useSelector} from 'react-redux';
import {Selector} from '../../state/selector';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import {useNavigate} from 'react-router';
import ManageCompanyEmployeesDialog from './ManageCompanyEmployeesDialog';
import QuizIcon from '@mui/icons-material/Quiz';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SetCompanyTesterDialog from './SetCompanyTesterDialog';
import {CompanyStatus} from '../../state/types/Company';
import CompanyStatusOverview from './AdminPage/CompanyStatusOverview';
import VersionHistoryDialog from './AdminPage/VersionHistoryDialog';
import Api from '../../api';
// import SetTrustYouScoreDialog from './SetTrustYouScoreDialog';
import DownloadReportMenu from './DownloadReportMenu';
import SetCompanyHiddenDialog from './SetCompanyHiddenDialog';
import Command from '../../state/actions/command';
import {DateTime} from 'luxon';
import {testCategories} from '../../util/testCategoryType';
import { Link } from 'react-router-dom';
import RerenderCompanyContractDialog from './RerenderCompanyContractDialog';
import {calculateBGScore, getHighestVotes} from '../../util/bgScore';

const buildVersionRange = (max: number): number[] =>
    Array.from({ length: max }, (_, i) => i + 1);

const companyParticipatesInVersion = (company: any, status: any, version: number): boolean => {
    const booking = company.bookings?.[version];
    const hasBooking = booking?.qualityCheck || booking?.marketing;
    const hasTests = status?.selfAssessment?.[version]
        || status?.websiteCheck?.[version] || status?.mysteryTest?.[version];
    return !!(hasBooking || hasTests);
};

const styles = {
    root: {
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
};

const getUnfilteredRows = ({ apiRef }: GridCsvGetRowsToExportParams) =>
    gridSortedRowIdsSelector(apiRef);

const AdminPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const companyList = useSelector(Selector.Company.companyList);
    const userList = useSelector(Selector.User.users);
    const companyStatusMap = useSelector(Selector.Company.companyStatusMap);
    const userId = useSelector(Selector.User.userId);
    const isAdmin = useSelector(Selector.User.isAdmin);
    const isEditor = useSelector(Selector.User.isEditor);
    const isTester = useSelector(Selector.User.isTester);
    const activeVersion = useSelector(Selector.QuestionList.activeVersion) || 5;
    const [selectedVersion, setSelectedVersion] = useState<number>(activeVersion);
    const [companyIdForHistory, setCompanyIdForHistory] = useState<string | undefined>();
    // Pure editors default to "Nur Teilnehmer" with archived hidden; admin keeps the full view.
    const isEditorOnly = isEditor && !isAdmin;
    const [showHidden, setShowHidden] = useState<boolean>(!isEditorOnly);
    const [onlyParticipating, setOnlyParticipating] = useState<boolean>(isEditorOnly);
    const [addNewCompanyDialogOpen, setAddNewCompanyDialogOpen] = useState<boolean>(false);
    const [companyIdToManageEmployeesFor, setCompanyIdToManageEmployeesFor] = useState<string>();
    const [companyIdToSetTesterFor, setCompanyIdToSetTesterFor] = useState<string>();
    // const [companyIdToSetTrustYouScore, setCompanyIdToSetTrustYouScore] = useState<string>();
    // const [companyStatusToSetTrustYouScore, setCompanyStatusToSetTrustYouScore] = useState<any>();
    const [companyIdToSetHidden, setCompanyIdToSetHidden] = useState<string>();
    const [companyIdToRerenderContract, setCompanyIdToRerenderContract] = useState<string>();
    const [mysteryTestIdToDownloadReport, setMysteryTestIdToDownloadReport] = useState<string>();
    const [companyIdToDownloadContract, setCompanyIdToDownloadContract] = useState<string>();
    const [downloadReportButton, setDownloadReportButton] = React.useState<null | HTMLElement>(null);
    const [reportAvailable, setReportAvailable] = useState<boolean>(false);
    const downloadReportOpen = Boolean(downloadReportButton);

    const loadUserData = async () => {
        const userIds: any[] = [];
        const loadedUsers: any[] = [];
        Object.keys(userList).forEach(key => {
            loadedUsers.push(key);
        });
        companyList.forEach(company => {
            if (!company.hidden && company.tester &&
                !userIds.includes(company.tester) && !loadedUsers.includes(company.tester))
            {
                userIds.push(company.tester);
                dispatch(Command.User.fetchUser({userId: company.tester}));
            }
        });
    };
    useEffect(() => {
        if (companyList.length > 0) {
            loadUserData();
        }
    }, [companyList]);

    useEffect(() => {
        setSelectedVersion(activeVersion);
    }, [activeVersion]);

    const generateMysteryTestReport = (mysteryTestId: string) => {
        // @todo run through sagas
        Api.Command.generateMysteryTestReport({ mysteryTestId });
    };

    const rows: GridRowsProp = companyList
        .filter(company => showHidden || !company.hidden)
        .filter(company => !onlyParticipating
            || companyParticipatesInVersion(company, companyStatusMap[company.id], selectedVersion))
        .map(company => ({
            ...company,
            companyStatus: companyStatusMap[company.id],
        }));

    const highestVotes = getHighestVotes(companyList, selectedVersion);

    /* eslint-disable max-len */
    const columns: GridColDef[] = [
        { field: 'restaurantName', headerName: 'Unternehmen', minWidth: 200, flex: 3 },
        { field: 'zip', headerName: 'Plz', minWidth: 133, flex: 2, hide: true},
        { field: 'city', headerName: 'Ort', minWidth: 133, flex: 2 },
        { field: 'street', headerName: 'Strasse', minWidth: 133, flex: 2, hide: true },
        { field: 'phone', headerName: 'Telefon', minWidth: 133, flex: 2 },
        { field: 'email', headerName: 'E-Mail', minWidth: 200, flex: 3 },
        { field: 'contactPerson', headerName: 'Ansprechpartner', minWidth: 200, flex: 3 },
        { field: 'travelRegion', headerName: 'Reiseregion', minWidth: 133, flex: 2 },
        {
            field: 'testCategory',
            headerName: 'Testkategorie',
            minWidth: 133,
            flex: 2,
            hide: true,
            renderCell: (params: GridRenderCellParams) => {
                return testCategories[params.row.testCategory ?? ''] ?? '-';
            },
        },
        { field: 'lastModified', headerName: 'Letzte Aktualisierung', minWidth: 133, flex: 2, disableExport: true },
        {
            field: 'companyStatus',
            headerName: 'Status',
            width: 225, //
            sortable: false,
            disableExport: true,
            renderCell: (params: GridRenderCellParams) => {
                const companyStatus: CompanyStatus|null = params.row.companyStatus;

                if (!companyStatus) {
                    return null;
                }

                /* TO-DO : unschärfe(0) / Gesamteindruck(0) für die Berechnung berücksichtigen */
                return (
                    <CompanyStatusOverview
                        status={companyStatus}
                        version={selectedVersion}
                        booking={params.row.bookings?.[selectedVersion]}
                        bgs={calculateBGScore(params.row, highestVotes, selectedVersion)}
                    />
                );
            },
            valueGetter: params => {
                let value = '';

                value += !params.row.companyStatus?.selfAssessment[selectedVersion] ? 'SA: nicht begonnen'
                    : params.row.companyStatus?.selfAssessment[selectedVersion].submitted ? 'SA: eingereicht' : 'SA: begonnen';
                value += '\n';
                value += !params.row.companyStatus?.websiteCheck[selectedVersion] ? 'WC: nicht begonnen'
                    : params.row.companyStatus?.websiteCheck[selectedVersion].submitted ? 'WC: eingereicht' : 'WC: begonnen';
                value += '\n';
                value += !params.row.companyStatus?.mysteryTest[selectedVersion] ? 'MT: nicht begonnen'
                    : params.row.companyStatus?.mysteryTest[selectedVersion].submitted ? 'MT: eingereicht' : 'MT: begonnen';
                value += '\n';
                /*value += !params.row.companyStatus?.trustYouScore[selectedVersion] ? 'TY: nicht begonnen'
                    : params.row.companyStatus?.trustYouScore[selectedVersion]?.score ? 'TY: eingereicht' : 'TY: begonnen';
                */
                value += 'GOOGLE';
                return value;
            },
        },
        {
            field: 'selfAssassmentScoreExport',
            headerName: 'selfAssassment',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                value += (companyStatus.selfAssessment[selectedVersion] && companyStatus.selfAssessment[selectedVersion].submitted) ?
                    companyStatus.selfAssessment[selectedVersion].score : ' - ';
                return value;
            },
        },
        {
            field: 'selfAssassmentMaxScoreExport',
            headerName: 'von maximal',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                value += (companyStatus.selfAssessment[selectedVersion] && companyStatus.selfAssessment[selectedVersion].submitted) ?
                    companyStatus.selfAssessment[selectedVersion].achievableScore : ' - ';
                return value;
            },
        },
        {
            field: 'selfAssassmentPercentExport',
            headerName: 'Prozentsatz',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                if(companyStatus.selfAssessment[selectedVersion] && companyStatus.selfAssessment[selectedVersion].submitted){
                    const percent = Math.round(
                        (100 * companyStatus.selfAssessment[selectedVersion].score!) /
                        (companyStatus.selfAssessment[selectedVersion].achievableScore!));
                    value += percent;
                } else {
                    value += ' - ';
                }
                return value;
            },
        },
        {
            field: 'selfAssessmentStartDate',
            headerName: 'Begonnen',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                if(companyStatus.selfAssessment[selectedVersion]){
                    const dateString = companyStatus.selfAssessment[selectedVersion].startedAt;
                    if(!dateString) {
                        return '';
                    }
                    value += DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm');
                } else {
                    value += ' - ';
                }
                return value;
            },
        },
        {
            field: 'selfAssessmentFinishedDate',
            headerName: 'Abgeschlossen',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                if(companyStatus.selfAssessment[selectedVersion] && companyStatus.selfAssessment[selectedVersion].submitted){
                    const dateString = companyStatus.selfAssessment[selectedVersion].finishedAt;
                    if(!dateString) {
                        return '';
                    }
                    value += DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm');
                } else {
                    value += ' - ';
                }
                return value;
            },
        },
        {
            field: 'websiteCheckScoreExport',
            headerName: 'websiteCheck',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                value += companyStatus.websiteCheck[selectedVersion] && companyStatus.websiteCheck[selectedVersion].submitted ?
                    companyStatus.websiteCheck[selectedVersion].score : ' - ';
                return value;
            },

        },
        {
            field: 'websiteCheckMaxScoreExport',
            headerName: 'von maximal',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                value += companyStatus.websiteCheck[selectedVersion] && companyStatus.websiteCheck[selectedVersion].submitted ?
                    companyStatus.websiteCheck[selectedVersion].achievableScore : ' - ';
                return value;
            },

        },
        {
            field: 'websiteCheckPercentExport',
            headerName: 'Prozentsatz',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                if(companyStatus.websiteCheck[selectedVersion] && companyStatus.websiteCheck[selectedVersion].submitted){
                    const percent = Math.round(
                        (100 * companyStatus.websiteCheck[selectedVersion].score!) /
                        (companyStatus.websiteCheck[selectedVersion].achievableScore!));
                    value += percent;
                } else {
                    value += ' - ';
                }
                return value;
            },
        },
        {
            field: 'websiteCheckStartDate',
            headerName: 'Begonnen',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                if(companyStatus.websiteCheck[selectedVersion]){
                    const dateString = companyStatus.websiteCheck[selectedVersion].startedAt;
                    if(!dateString) {
                        return '';
                    }
                    value += DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm');
                } else {
                    value += ' - ';
                }
                return value;
            },
        },
        {
            field: 'websiteCheckFinishedDate',
            headerName: 'Abgeschlossen',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                if(companyStatus.websiteCheck[selectedVersion] && companyStatus.websiteCheck[selectedVersion].submitted){
                    const dateString = companyStatus.websiteCheck[selectedVersion].finishedAt;
                    if(!dateString) {
                        return '';
                    }
                    value += DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm');
                } else {
                    value += ' - ';
                }
                return value;
            },
        },
        {
            field: 'mysteryTestScoreExport',
            headerName: 'mysteryTest',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                value += companyStatus.mysteryTest[selectedVersion] && companyStatus.mysteryTest[selectedVersion].submitted ?
                    companyStatus.mysteryTest[selectedVersion].score : ' - ';
                return value;
            },

        },
        /* 'Gesamteindruck'(0)(AchievablePoints) and 'unschärfe'(0) of the questions for MysteryTest */
        {
            field: 'mysteryTestMaxScoreExport',
            headerName: 'von maximal',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                value += companyStatus.mysteryTest[selectedVersion] && companyStatus.mysteryTest[selectedVersion].submitted ?
                    companyStatus.mysteryTest[selectedVersion].achievableScore! : ' - ';
                return value;
            },

        },
        {
            field: 'mysteryTestPercentExport',
            headerName: 'Prozentsatz',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                if(companyStatus.mysteryTest[selectedVersion] && companyStatus.mysteryTest[selectedVersion].submitted){
                    const percent = Math.round(
                        (100 * companyStatus.mysteryTest[selectedVersion].score!) /
                        (companyStatus.mysteryTest[selectedVersion].achievableScore!));
                    value += percent;
                } else {
                    value += ' - ';
                }
                return value;
            },
        },
        {
            field: 'mysteryTestStartDate',
            headerName: 'Begonnen',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                if(companyStatus.mysteryTest[selectedVersion]){
                    const dateString = companyStatus.mysteryTest[selectedVersion].startedAt;
                    if(!dateString) {
                        return '';
                    }
                    value += DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm');
                } else {
                    value += ' - ';
                }
                return value;
            },
        },
        {
            field: 'mysteryTestFinishedDate',
            headerName: 'Abgeschlossen',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                if(companyStatus.mysteryTest[selectedVersion] && companyStatus.mysteryTest[selectedVersion].submitted){
                    const dateString = companyStatus.mysteryTest[selectedVersion].finishedAt;
                    if(!dateString) {
                        return '';
                    }
                    value += DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm');
                } else {
                    value += ' - ';
                }
                return value;
            },
        },
        /*{
            field: 'trustYouScoreExport',
            headerName: 'Trust You Score',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                value += companyStatus.trustYouScore[selectedVersion] && companyStatus.trustYouScore[selectedVersion].score ?
                    companyStatus.trustYouScore[selectedVersion].score! : ' - ';
                return value;
            },

        },
        {
            field: 'trustYouScorePercentExport',
            headerName: '% (5.0)',
            sortable: false,
            hide: true,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){return '-';}
                const companyStatus = companyStatusMap[params.id];
                if(companyStatus == null){return '-';}
                if(companyStatus.trustYouScore[selectedVersion] && companyStatus.trustYouScore[selectedVersion].score){
                    const percent = Math.round(
                        (100 * companyStatus.trustYouScore[selectedVersion].score!) /
                        (5));
                    value += percent;
                } else {
                    value += ' - ';
                }
                return value;
            },
        },*/
        {
            field: 'testerExport',
            headerName: 'Tester',
            width: 0,
            flex: 0,
            sortable: false,
            hide: false,
            renderCell: (params: GridRenderCellParams) => { return ''; },
            valueFormatter: (params: GridValueFormatterParams) => {
                let value = '';
                if(!params.id){
                    return '-';
                } else {
                    const company = companyList.find(company => company.id === params.id);
                    if(company && company.tester && userList[company.tester]){
                        const user = userList[company.tester];
                        value += user.email + ', ' + user.firstName + ' ' + user.lastName;
                    } else {
                        value += ' - ';
                    }
                    return value;
                }
            },
        },
        {
            field: 'actions',
            headerName: 'Aktionen',
            width: isAdmin ? 476 : 156, // + 40 for each additional action IconButton
            sortable: false,
            disableExport: true,
            renderCell: (params: GridRenderCellParams) => {
                const showEditAction = Boolean(isAdmin || params.row.employees.includes(userId));
                const showTesterViewAction = !showEditAction && isTester && params.row.tester === userId;
                // Editors have read-only access to every company's Stammdaten.
                const showEditorViewAction = !showEditAction && !showTesterViewAction && isEditor;
                const companyStatus: CompanyStatus|null = params.row.companyStatus;

                return (<>
                    {showEditAction && (
                        <Button
                            variant={'contained'}
                            size={'small'}
                            children={'Stammdaten'}
                            startIcon={<EditIcon />}
                            onClick={() => navigate(`/companies/${params.row.id}/base-data`)}
                        />
                    )}
                    {(showTesterViewAction || showEditorViewAction) && (
                        <Button
                            variant={'contained'}
                            size={'small'}
                            children={showEditorViewAction ? 'Stammdaten' : 'Details anzeigen'}
                            onClick={() => navigate(`/companies/${params.row.id}/base-data`)}
                        />
                    )}
                    {isAdmin && (
                        <Tooltip title={'Mitarbeiter verwalten'}>
                            <IconButton onClick={() => setCompanyIdToManageEmployeesFor(params.row.id)}>
                                <PersonIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {isAdmin && (
                        <Tooltip title={'Tester auswählen'}>
                            <IconButton onClick={() => setCompanyIdToSetTesterFor(params.row.id)}>
                                <QuizIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {isAdmin && (
                        <Tooltip title={'Report generieren'}>
                            <IconButton
                                onClick={() => {
                                    const mtId = companyStatus?.mysteryTest?.[selectedVersion]?.mysteryTestId;
                                    if (mtId) generateMysteryTestReport(mtId);
                                }}
                                disabled={!companyStatus?.mysteryTest?.[selectedVersion]?.submitted}
                                children={<SummarizeIcon />}
                            />
                        </Tooltip>
                    )}
                    {isAdmin && (
                        <Tooltip
                            title={!companyStatus?.mysteryTest?.[selectedVersion]?.submitted ?
                                'Vertrag herunterladen' : 'Report/Vertrag herunterladen'}
                        >
                            <IconButton
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                    setMysteryTestIdToDownloadReport(companyStatus!.mysteryTest?.[selectedVersion]?.mysteryTestId);
                                    setCompanyIdToDownloadContract(params.row.id!);
                                    setDownloadReportButton(event.currentTarget);
                                    setReportAvailable(!!companyStatus?.mysteryTest?.[selectedVersion]?.submitted);
                                }}
                                children={
                                    <DownloadIcon
                                        color={
                                            !companyStatus?.mysteryTest[selectedVersion]?.submitted ?
                                                'action' : 'primary'
                                        }
                                    />
                                }
                            />
                        </Tooltip>
                    )}
                    {isAdmin && (
                        <Tooltip title={`Vertrag neu generieren (v${selectedVersion})`}>
                            <IconButton
                                onClick={() => setCompanyIdToRerenderContract(params.row.id)}
                                disabled={!params.row.bookings?.[selectedVersion]?.qualityCheck && !params.row.bookings?.[selectedVersion]?.marketing}
                            >
                                <SummarizeIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {/* isAdmin && (
                        <Tooltip title={'TrustYou Score setzen'}>
                            <IconButton
                                onClick={() => {
                                    setCompanyStatusToSetTrustYouScore(params.row.companyStatus!);
                                    setCompanyIdToSetTrustYouScore(params.row.id!);}
                                }
                                children={(
                                    <img
                                        src={Assets.trustYouBubbles}
                                        alt={'trustYouScore'}
                                        width={24}
                                        height={24}
                                        style={{
                                            objectFit: 'contain',
                                            filter: Boolean(companyStatus?.trustYouScore[selectedVersion]) ? 'grayscale(1)' : undefined,
                                        }}
                                    />
                                )}
                            />
                        </Tooltip>
                    ) */}
                    {isAdmin && (
                        <Tooltip title={'Versionshistorie'}>
                            <IconButton
                                onClick={() => setCompanyIdForHistory(params.row.id)}
                                children={<HistoryIcon />}
                            />
                        </Tooltip>
                    )}
                    {isAdmin && (
                        <Tooltip title={params.row.hidden ? 'Unternehmen einblenden' : 'Unternehmen ausblenden'}>
                            <IconButton
                                onClick={() => setCompanyIdToSetHidden(params.row.id!)}
                                children={params.row.hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            />
                        </Tooltip>
                    )}
                    {isEditor && !!companyStatus?.mysteryTest?.[selectedVersion]?.submitted && (
                        <Tooltip title={'Unternehmensreport'}>
                            <IconButton
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                    window.open(window.location.origin + '/api/reports/mystery-test/' +
                                        companyStatus!.mysteryTest?.[selectedVersion]?.mysteryTestId + '_company');
                                }}
                                children={<DownloadIcon color={'primary'} />}
                            />
                        </Tooltip>
                    )}
                </>);
            },
        },
    ];

    // Overview and BG-Score Export
    const headers = [
        { label: 'Betrieb', key: 'name' },
        { label: 'E-Mail', key: 'email' },
        { label: 'Kontakt', key: 'contact' },
        { label: 'Mystery Test', key: 'mysteryTest' },
        { label: 'Mystery Test in %', key: 'mysteryTestPercentage' },
        { label: 'Wichtung Mystery Test', key: 'mysteryTestWeight' },
        { label: 'Versuche Mystery  Test', key: 'mysteryTestTrys' },
        /*
        { label: 'TrustYou Score', key: 'trustYouScore' },
        { label: 'TrustYou Score in %', key: 'trustYouScorePercentage' },
        { label: 'Wichtung TrustYou Score', key: 'trustYouScoreWeight' },
        */
        { label: 'Selbstauskunft', key: 'selfAssessment' },
        { label: 'Selbstauskunft in %', key: 'selfAssessmentPercentage' },
        { label: 'Wichtung Selbstauskunft', key: 'selfAssessmentWeight' },
        { label: 'Website-Check', key: 'websiteCheck' },
        { label: 'Website-Check in %', key: 'websiteCheckPercentage' },
        { label: 'Wichtung Website-Check', key: 'websiteCheckWeight' },
        { label: 'BGS - Favorite Restaurant Voting', key: 'bgs'},
        { label: 'BG Score', key: 'BGScore' },
        { label: 'Kategorie', key: 'testCategory' },
        { label: 'Reiseregion', key: 'travelRegion' },
    ];
    const data = companyList
        .filter(company => showHidden || !company.hidden)
        .filter(company => !onlyParticipating
            || companyParticipatesInVersion(company, companyStatusMap[company.id], selectedVersion))
        .map(company => {
            const companyStatus = companyStatusMap[company.id];
            const mt = companyStatus?.mysteryTest[selectedVersion];
            const sa = companyStatus?.selfAssessment[selectedVersion];
            const wc = companyStatus?.websiteCheck[selectedVersion];
            // const ts = companyStatus?.trustYouScore[selectedVersion];

            const mtP = mt?.score && mt?.achievableScore ?
                Number((mt.score * 100 / mt.achievableScore).toFixed(2)) : 0;
            /* const tsP = ts?.score ?
                Number((ts.score * 100 / 5).toFixed(2)) : 0; */
            const saP = sa?.score && sa?.achievableScore ?
                Number((sa.score * 100 / sa.achievableScore).toFixed(2)) : 0;
            const wcP = wc?.score && wc?.achievableScore ?
                Number((wc.score * 100 / wc.achievableScore).toFixed(2)) : 0;

            const mtW = 0.6, saW = 0.1, wcW = 0.1, tsW = 0.2;

            const bgs = calculateBGScore(company, highestVotes, selectedVersion);
            const bgScore = (mtP * mtW) + (saP * saW) + (wcP * wcW) + (bgs * tsW);

            return {
                name: company.restaurantName,
                email: company.email,
                contact: company.contactPerson,
                mysteryTest: mt?.score && mt?.achievableScore
                    ? `${mt.score} / ${mt.achievableScore}` : '-',
                mysteryTestPercentage: `${mtP.toFixed(2)}%`,
                mysteryTestWeight: mtW,
                mysteryTestTrys: mt?.attempt || 1,
                /*
                trustYouScore: ts?.score ? `${ts.score.toFixed(3)} / 5` : '-',
                trustYouScorePercentage: `${tsP.toFixed(2)}%`,
                trustYouScoreWeight: tsW,
                */
                selfAssessment: sa?.score && sa?.achievableScore
                    ? `${sa.score} / ${sa.achievableScore}` : '-',
                selfAssessmentPercentage: `${saP.toFixed(2)}%`,
                selfAssessmentWeight: saW,
                websiteCheck: wc?.score && wc?.achievableScore
                    ? `${wc.score} / ${wc.achievableScore}` : '-',
                websiteCheckPercentage: `${wcP.toFixed(2)}%`,
                websiteCheckWeight: wcW,
                bgs: bgs,
                BGScore: `${bgScore.toFixed(2)}%`,
                testCategory: company.testCategory
                    ? testCategories[company.testCategory] : '-',
                travelRegion: company.travelRegion,
            };
        });
    const date = DateTime.now().toFormat('yyyyMMdd-HHmmss');
    const csvReport = {
        data: data,
        headers: headers,
        separator: ';',
        filename: `${date}-score-export.csv`,
    };

    return (
        <Box sx={styles.root}>
            <Box>
                <Typography variant={'h1'}>
                    Teilnehmende Unternehmen
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', alignItems: 'center'}}>
                    {(isAdmin || isEditor) && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={onlyParticipating}
                                    onChange={(e) => setOnlyParticipating(e.target.checked)}
                                    size={'small'}
                                />
                            }
                            label={'Nur Teilnehmer'}
                        />
                    )}
                    {(isAdmin || isEditor) && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showHidden}
                                    onChange={(e) => setShowHidden(e.target.checked)}
                                    size={'small'}
                                />
                            }
                            label={'Archivierte anzeigen'}
                        />
                    )}
                    {(isAdmin || isEditor) && (
                        <FormControl size={'small'} sx={{ minWidth: 180 }}>
                            <InputLabel>Version</InputLabel>
                            <Select
                                value={selectedVersion}
                                label={'Version'}
                                onChange={(e: SelectChangeEvent<number>) => setSelectedVersion(Number(e.target.value))}
                            >
                                {buildVersionRange(activeVersion).map(v => (
                                    <MenuItem key={v} value={v}>
                                        Version {v}{v === activeVersion ? ' (aktuell)' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
                    {rows.length === 0 && !isAdmin && (
                        <Link 
                            to={'/companies/registration'}
                            style={{ textDecoration: 'none', color: 'red' }}
                        >
                            <Button 
                                children={'Unternehmen registrieren'}
                                variant={'contained'}
                            />
                        </Link>
                    )}
                    {isAdmin && (
                        <Button
                            children={'Gesamt Report'}
                            variant={'contained'}
                            onClick={() => {
                                window.open(window.location.origin + '/api/reports/over-all-report/preview/' + selectedVersion);
                            }}
                        />
                    )}
                    {isAdmin && React.createElement(
                        CSVLink as any,
                        {...csvReport, onClick: () => {}},
                        <Button
                            children={'Übersicht exportieren'}
                            variant={'contained'}
                        />,
                    )}
                    {isAdmin && (
                        <Button
                            onClick={() => setAddNewCompanyDialogOpen(true)}
                            children={'Unternehmen hinzufügen'}
                            variant={'contained'}
                        />
                    )}
                </Box>
            </Box>
            <Card sx={{ marginTop: 2, flex: 1, width: '100%' }}>
                <DataGrid
                    columns={columns}
                    rows={rows}
                    disableSelectionOnClick={true}
                    disableColumnFilter={true}
                    disableColumnSelector={true}
                    disableDensitySelector={true}
                    disableColumnMenu={true}
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 200 },
                            csvOptions: {
                                delimiter: ';',
                                fileName: 'Brandenburger Gastlichkeit',
                                allColumns: true,
                                getRowsToExport: getUnfilteredRows,
                                disableToolbarButton: !isAdmin,
                            },
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                />
            </Card>

            {/* Dialogs */}
            <AddNewCompanyDialog
                open={addNewCompanyDialogOpen}
                onClose={() => setAddNewCompanyDialogOpen(false)}
            />
            <ManageCompanyEmployeesDialog
                open={Boolean(companyIdToManageEmployeesFor)}
                companyId={companyIdToManageEmployeesFor}
                onClose={() => setCompanyIdToManageEmployeesFor(undefined)}
            />
            <SetCompanyTesterDialog
                open={Boolean(companyIdToSetTesterFor)}
                companyId={companyIdToSetTesterFor}
                onClose={() => setCompanyIdToSetTesterFor(undefined)}
            />
            {/* Boolean(companyIdToSetTrustYouScore && companyStatusToSetTrustYouScore) &&
                <SetTrustYouScoreDialog
                    open={Boolean(companyIdToSetTrustYouScore && companyStatusToSetTrustYouScore)}
                    companyId={companyIdToSetTrustYouScore}
                    companyStatus={companyStatusToSetTrustYouScore}
                    version={selectedVersion}
                    onClose={() => {
                        setCompanyIdToSetTrustYouScore(undefined);
                        setCompanyStatusToSetTrustYouScore(undefined);
                    }}
                />
            */}
            <DownloadReportMenu
                open={downloadReportOpen}
                mysteryTestId={mysteryTestIdToDownloadReport}
                mysteryTestSubmitted={reportAvailable}
                companyId={companyIdToDownloadContract}
                version={selectedVersion}
                anchorEl={downloadReportButton}
                onClose={() => {
                    setDownloadReportButton(null);
                    setMysteryTestIdToDownloadReport(undefined);
                }}
            />
            {isAdmin && (
                <RerenderCompanyContractDialog
                    open={Boolean(companyIdToRerenderContract)}
                    companyId={companyIdToRerenderContract}
                    version={selectedVersion}
                    onClose={() => {setCompanyIdToRerenderContract(undefined);}}
                />
            )}
            <VersionHistoryDialog
                open={Boolean(companyIdForHistory)}
                companyId={companyIdForHistory}
                companyStatusMap={companyStatusMap}
                companyList={companyList}
                activeVersion={activeVersion}
                onClose={() => setCompanyIdForHistory(undefined)}
            />
            <SetCompanyHiddenDialog
                open={Boolean(companyIdToSetHidden)}
                companyId={companyIdToSetHidden}
                onClose={() => setCompanyIdToSetHidden(undefined)}
            />
        </Box>
    );
};

export default AdminPage;
